import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import { z } from 'zod';
import { addDays, today, weekStart } from '@mkcore/shared';
import { DETAILED_COLUMNS, GROUP_KEYS } from '@mkcore/reporting/schema';
import { Badge, Button, Card, Checkbox, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton, TBody, THead, Table, Tabs, TabsContent, TabsList, TabsTrigger, Td, Th, Tr, buttonVariants, cn } from '@mkcore/ui';
import { formatDate, formatDuration, formatHours, formatMoney } from '@/lib/format';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { billabilityQuery, budgetQuery, detailedQuery, exportUrl, summaryQuery, unbilledQuery, weekReportQuery } from '@/features/reporting/queries';
import { Dot, ProjectSelect, WorkTypeSelect } from '@/features/time/pickers';
import { SupportReport } from '@/features/reporting/support-report';
import { AgreementsEnding } from '@/features/reporting/agreements-ending';
import { MarginTab, SalesTab } from '@/features/reporting/insight';
import { BilledElsewhereDialog } from '@/features/billing/billed-elsewhere-dialog';

const TABS = ['detailed', 'summary', 'week', 'billability', 'budget', 'unbilled', 'margin', 'sales', 'support', 'agreements'] as const;
const search = z.object({
  tab: z.enum(TABS).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  everyone: z.boolean().optional(),
  organisationId: z.string().optional(),
  projectId: z.string().optional(),
  workTypeId: z.string().optional(),
  tag: z.string().optional(),
  billable: z.boolean().optional(),
  rounded: z.boolean().optional(),
  q: z.string().optional(),
  groupBy: z.string().optional(),
  by: z.enum(['month', 'week', 'person']).optional(),
  months: z.coerce.number().int().min(1).max(24).optional(),
  marginBy: z.enum(['organisation', 'engagement', 'month']).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/reports')({ validateSearch: search, component: Reports });

const monthStart = (d: string) => `${d.slice(0, 7)}-01`;
const monthEnd = (d: string) => {
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  return addDays(m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`, -1);
};

/** The reports page (CO-0301): filters in the URL, one tab per report, export as CSV or XLSX (CO-0298). */
function Reports() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canAll = useCan('reporting:readAll');
  const canExport = useCan('reporting:export');
  const canSupport = useCan('support:read');
  const canAgreements = useCan('engagements:read');
  const tab = s.tab ?? 'detailed';
  const now = today();
  const from = s.from ?? monthStart(now);
  const to = s.to ?? monthEnd(now);
  const set = (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const filters = {
    from,
    to,
    ...(s.everyone && canAll && { everyone: true }),
    ...(s.organisationId && { organisationId: s.organisationId }),
    ...(s.projectId && { projectId: s.projectId }),
    ...(s.workTypeId && { workTypeId: s.workTypeId }),
    ...(s.tag && { tag: s.tag }),
    ...(s.billable !== undefined && { billable: s.billable }),
    ...(s.rounded && { rounded: true }),
    ...(s.q && { q: s.q }),
  };
  const exportParams = { ...filters, everyone: filters.everyone ? true : undefined };
  const presets: [string, string, string][] = [
    [t('reports.presets.thisWeek'), weekStart(now), addDays(weekStart(now), 6)],
    [t('reports.presets.thisMonth'), monthStart(now), monthEnd(now)],
    [t('reports.presets.lastMonth'), monthStart(addDays(monthStart(now), -1)), addDays(monthStart(now), -1)],
    [t('reports.presets.thisYear'), `${now.slice(0, 4)}-01-01`, `${now.slice(0, 4)}-12-31`],
  ];

  return (
    <>
      <PageHeader title={t('reports.title')} description={t('reports.description')} />
      <Card className="mb-4 grid gap-3 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('time.from')} className="w-40">{(p) => <Input {...p} type="date" value={from} onChange={(e) => e.target.value && set({ from: e.target.value })} />}</Field>
          <Field label={t('time.to')} className="w-40">{(p) => <Input {...p} type="date" value={to} onChange={(e) => e.target.value && set({ to: e.target.value })} />}</Field>
          <div className="flex flex-wrap gap-1 pb-2">
            {presets.map(([label, f, tt]) => (
              <Button key={label} size="sm" variant={from === f && to === tt ? 'secondary' : 'ghost'} onClick={() => set({ from: f, to: tt })}>
                {label}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t('time.fields.project')} className="w-56">{(p) => <ProjectSelect {...p} value={s.projectId ?? ''} emptyLabel={t('common.all')} onChange={(v) => set({ projectId: v || undefined })} />}</Field>
          <Field label={t('time.fields.workType')} className="w-44">{(p) => <WorkTypeSelect {...p} value={s.workTypeId ?? ''} emptyLabel={t('common.all')} onChange={(v) => set({ workTypeId: v || undefined })} />}</Field>
          <Field label={t('time.fields.tag')} className="w-32">{(p) => <Input {...p} value={s.tag ?? ''} onChange={(e) => set({ tag: e.target.value || undefined })} />}</Field>
          <Field label={t('time.fields.billable')} className="w-32">
            {(p) => (
              <NativeSelect {...p} value={s.billable === undefined ? '' : String(s.billable)} onChange={(e) => set({ billable: e.target.value === '' ? undefined : e.target.value === 'true' })}>
                <option value="">{t('common.all')}</option>
                <option value="true">{t('common.yes')}</option>
                <option value="false">{t('common.no')}</option>
              </NativeSelect>
            )}
          </Field>
          <Field label={t('common.search')} className="w-48">{(p) => <Input {...p} value={s.q ?? ''} placeholder={t('time.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}</Field>
          <label className="flex items-center gap-2 pb-2 text-sm" title={t('reports.roundedHint')}>
            <Checkbox checked={!!s.rounded} onCheckedChange={(v) => set({ rounded: v === true || undefined })} /> {t('reports.rounded')}
          </label>
          {canAll && (
            <label className="flex items-center gap-2 pb-2 text-sm">
              <Checkbox checked={!!s.everyone} onCheckedChange={(v) => set({ everyone: v === true || undefined })} /> {t('reports.everyone')}
            </label>
          )}
        </div>
      </Card>
      <Tabs value={tab} onValueChange={(v) => set({ tab: v as Search['tab'] })}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="detailed">{t('reports.tabs.detailed')}</TabsTrigger>
          <TabsTrigger value="summary">{t('reports.tabs.summary')}</TabsTrigger>
          <TabsTrigger value="week">{t('reports.tabs.week')}</TabsTrigger>
          <TabsTrigger value="billability">{t('reports.tabs.billability')}</TabsTrigger>
          {canAll && <TabsTrigger value="budget">{t('reports.tabs.budget')}</TabsTrigger>}
          {canAll && <TabsTrigger value="unbilled">{t('reports.tabs.unbilled')}</TabsTrigger>}
          {canAll && <TabsTrigger value="margin">{t('reports.tabs.margin')}</TabsTrigger>}
          {canAll && <TabsTrigger value="sales">{t('reports.tabs.sales')}</TabsTrigger>}
          {canSupport && <TabsTrigger value="support">{t('reports.tabs.support')}</TabsTrigger>}
          {canAgreements && <TabsTrigger value="agreements">{t('reports.tabs.agreements')}</TabsTrigger>}
        </TabsList>
        <TabsContent value="detailed">{tab === 'detailed' && <Detailed filters={filters} exportParams={exportParams} canExport={canExport} everyone={!!filters.everyone} message={message} />}</TabsContent>
        <TabsContent value="summary">{tab === 'summary' && <Summary filters={filters} exportParams={exportParams} groupBy={s.groupBy ?? 'project'} setGroupBy={(g) => set({ groupBy: g })} canExport={canExport} message={message} />}</TabsContent>
        <TabsContent value="week">{tab === 'week' && <WeekTab filters={filters} message={message} />}</TabsContent>
        <TabsContent value="billability">{tab === 'billability' && <Billability filters={filters} by={s.by ?? 'month'} setBy={(b) => set({ by: b })} message={message} />}</TabsContent>
        <TabsContent value="budget">{tab === 'budget' && canAll && <Budget organisationId={s.organisationId} projectId={s.projectId} message={message} />}</TabsContent>
        <TabsContent value="unbilled">{tab === 'unbilled' && canAll && <Unbilled organisationId={s.organisationId} to={to} message={message} />}</TabsContent>
        <TabsContent value="margin">
          {tab === 'margin' && canAll && (
            <MarginTab
              from={from}
              to={to}
              organisationId={s.organisationId}
              by={s.marginBy ?? 'organisation'}
              setBy={(b) => set({ marginBy: b })}
              actions={canExport && <ExportButtons params={{ kind: 'margin', by: s.marginBy ?? 'organisation', from, to, organisationId: s.organisationId }} />}
            />
          )}
        </TabsContent>
        <TabsContent value="sales">
          {tab === 'sales' && canAll && <SalesTab from={from} to={to} organisationId={s.organisationId} actions={canExport && <ExportButtons params={{ kind: 'sales', from, to, organisationId: s.organisationId }} />} />}
        </TabsContent>
        <TabsContent value="support">{tab === 'support' && canSupport && <SupportReport from={from} to={to} organisationId={s.organisationId} message={message} />}</TabsContent>
        <TabsContent value="agreements">{tab === 'agreements' && canAgreements && <AgreementsEnding months={s.months ?? 3} />}</TabsContent>
      </Tabs>
    </>
  );
}

type Filters = { from: string; to: string; everyone?: boolean; organisationId?: string; projectId?: string; workTypeId?: string; tag?: string; billable?: boolean; rounded?: boolean; q?: string };
type Msg = (e: unknown) => string;

function TotalsBar({ totals, className }: { totals: { seconds: number; billableSeconds: number; nonBillableSeconds: number; sellCents: number; costCents: number; entries: number; agentSeconds: number }; className?: string }) {
  const { t } = useTranslation();
  const items: [string, string][] = [
    [t('reports.totals.entries'), String(totals.entries)],
    [t('reports.totals.hours'), formatDuration(totals.seconds)],
    [t('reports.totals.billable'), formatDuration(totals.billableSeconds)],
    [t('reports.totals.nonBillable'), formatDuration(totals.nonBillableSeconds)],
    [t('reports.totals.sell'), formatMoney(totals.sellCents)],
    [t('reports.totals.cost'), formatMoney(totals.costCents)],
    ...(totals.agentSeconds ? [[t('reports.totals.agent'), formatDuration(totals.agentSeconds)] as [string, string]] : []),
  ];
  return (
    <div className={cn('flex flex-wrap gap-x-6 gap-y-1 text-sm', className)} data-testid="report-totals">
      {items.map(([k, v]) => (
        <span key={k} className="text-fg-2">
          {k}: <span className="font-bold tabular-nums text-fg-1">{v}</span>
        </span>
      ))}
    </div>
  );
}

function ExportButtons({ params, disabled }: { params: Record<string, string | number | boolean | undefined>; disabled?: boolean }) {
  return (
    <div className="flex gap-2">
      {(['csv', 'xlsx', 'pdf'] as const).map((format) => (
        <a key={format} className={cn(buttonVariants({ variant: 'secondary', size: 'sm' }), disabled && 'pointer-events-none opacity-50')} href={exportUrl({ ...params, format })} download>
          <Download /> {format.toUpperCase()}
        </a>
      ))}
    </div>
  );
}

function Detailed({ filters, exportParams, canExport, everyone, message }: { filters: Filters; exportParams: Record<string, string | number | boolean | undefined>; canExport: boolean; everyone: boolean; message: Msg }) {
  const { t } = useTranslation();
  const q = useInfiniteQuery(detailedQuery({ ...filters, limit: 100 }));
  const defaultColumns = DETAILED_COLUMNS.filter((c) => !['id', 'source', 'durationSeconds', 'startedAt', 'endedAt', 'rateCents', 'currency', 'costAmountCents'].includes(c));
  const [columns, setColumns] = useState<string[]>(defaultColumns);
  const [pick, setPick] = useState(false);
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const totals = q.data?.pages[0]?.totals;
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (q.isLoading) return <Skeleton className="h-64" />;
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        {totals && <TotalsBar totals={totals} className="flex-1" />}
        {canExport && (
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setPick((v) => !v)} aria-pressed={pick}>
              {t('reports.columns')}
            </Button>
            <ExportButtons params={{ ...exportParams, kind: 'detailed', columns: columns.join(',') }} />
          </div>
        )}
      </div>
      {pick && (
        <Card className="flex flex-wrap gap-3 p-3 text-sm">
          {DETAILED_COLUMNS.map((c) => (
            <label key={c} className="flex items-center gap-1.5">
              <Checkbox checked={columns.includes(c)} onCheckedChange={(v) => setColumns(v === true ? DETAILED_COLUMNS.filter((x) => x === c || columns.includes(x)) : columns.filter((x) => x !== c))} />
              {t(`reports.column.${c}`)}
            </label>
          ))}
        </Card>
      )}
      {!rows.length ? (
        <EmptyState title={t('reports.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('time.fields.date')}</Th>
              {everyone && <Th>{t('reports.column.userName')}</Th>}
              <Th>{t('time.fields.project')}</Th>
              <Th className="hidden md:table-cell">{t('time.fields.workType')}</Th>
              <Th>{t('time.fields.description')}</Th>
              <Th className="text-right">{t('reports.totals.hours')}</Th>
              <Th className="hidden sm:table-cell">{t('time.fields.billable')}</Th>
              <Th className="hidden text-right lg:table-cell">{t('reports.totals.sell')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((e) => (
              <Tr key={e.id}>
                <Td className="tabular-nums">{formatDate(e.date)}</Td>
                {everyone && <Td>{e.userName}</Td>}
                <Td>
                  <span className="flex items-center gap-2">
                    <Dot colour={e.projectColour} /> {e.projectName}
                  </span>
                  <span className="block text-xs text-fg-3">{e.organisationName ?? t('projects.internal')}</span>
                </Td>
                <Td className="hidden md:table-cell">{e.workTypeName}</Td>
                <Td>
                  {e.description}
                  {e.tags.map((tag) => (
                    <Badge key={tag} tone="neutral" className="ml-1">
                      {tag}
                    </Badge>
                  ))}
                </Td>
                <Td className="text-right tabular-nums">{formatDuration(e.durationSeconds)}</Td>
                <Td className="hidden sm:table-cell">{e.billable ? t('common.yes') : t('common.no')}</Td>
                <Td className="hidden text-right tabular-nums lg:table-cell">{formatMoney(e.sellAmountCents)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
    </div>
  );
}

function Summary({ filters, exportParams, groupBy, setGroupBy, canExport, message }: { filters: Filters; exportParams: Record<string, string | number | boolean | undefined>; groupBy: string; setGroupBy: (g: string) => void; canExport: boolean; message: Msg }) {
  const { t } = useTranslation();
  const [k1 = 'project', k2 = ''] = groupBy.split(',');
  const keys = [k1, k2].filter(Boolean) as (typeof GROUP_KEYS)[number][];
  const q = useQuery(summaryQuery({ ...filters, groupBy: keys }));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  const two = keys.length > 1;
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('reports.groupBy')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={k1} onChange={(e) => setGroupBy([e.target.value, k2].filter(Boolean).join(','))}>
              {GROUP_KEYS.map((k) => (
                <option key={k} value={k}>
                  {t(`reports.group.${k}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('reports.thenBy')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={k2} onChange={(e) => setGroupBy([k1, e.target.value].filter(Boolean).join(','))}>
              <option value="">{t('common.none')}</option>
              {GROUP_KEYS.filter((k) => k !== k1).map((k) => (
                <option key={k} value={k}>
                  {t(`reports.group.${k}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <div className="flex-1" />
        {canExport && <ExportButtons params={{ ...exportParams, kind: 'summary', groupBy: keys.join(',') }} />}
      </div>
      {!q.data ? (
        <Skeleton className="h-64" />
      ) : !q.data.groups.length ? (
        <EmptyState title={t('reports.empty')} />
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                <Th>{t(`reports.group.${k1}`)}</Th>
                {two && <Th>{t(`reports.group.${k2}`)}</Th>}
                <Th className="text-right">{t('reports.totals.entries')}</Th>
                <Th className="text-right">{t('reports.totals.hours')}</Th>
                <Th className="text-right">{t('reports.totals.billable')}</Th>
                <Th className="hidden text-right md:table-cell">{t('reports.totals.nonBillable')}</Th>
                <Th className="text-right">{t('reports.totals.sell')}</Th>
                <Th className="hidden text-right lg:table-cell">{t('reports.totals.cost')}</Th>
              </Tr>
            </THead>
            <TBody>
              {q.data.groups.map((g, i) => (
                <Tr key={`${g.key}:${g.subKey}:${i}`}>
                  <Td className="font-bold">{g.label}</Td>
                  {two && <Td>{g.subLabel}</Td>}
                  <Td className="text-right tabular-nums">{g.entries}</Td>
                  <Td className="text-right tabular-nums">{formatDuration(g.seconds)}</Td>
                  <Td className="text-right tabular-nums">{formatDuration(g.billableSeconds)}</Td>
                  <Td className="hidden text-right tabular-nums md:table-cell">{formatDuration(g.nonBillableSeconds)}</Td>
                  <Td className="text-right tabular-nums">{formatMoney(g.sellCents)}</Td>
                  <Td className="hidden text-right tabular-nums lg:table-cell">{formatMoney(g.costCents)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          <TotalsBar totals={q.data.totals} />
        </>
      )}
    </div>
  );
}

function WeekTab({ filters, message }: { filters: Filters; message: Msg }) {
  const { t } = useTranslation();
  const q = useQuery(weekReportQuery(filters));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-64" />;
  const dayName = (i: number) => t(`workSettings.day.${i + 1}`);
  return (
    <div className="grid gap-6">
      {q.data.weeks.map((w) => (
        <section key={w.weekStart}>
          <h2 className="mb-2 text-sm font-bold uppercase tracking-caps text-fg-2">
            {formatDate(w.days[0]!)} – {formatDate(w.days[6]!)} <span className="font-normal normal-case tracking-normal text-fg-3">{formatDuration(w.totalSeconds)}</span>
          </h2>
          {!w.rows.length ? (
            <p className="text-sm text-fg-3">{t('reports.empty')}</p>
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>{t('time.fields.project')}</Th>
                  {w.days.map((d, i) => (
                    <Th key={d} className="text-right">
                      {dayName(i)} {d.slice(8)}
                    </Th>
                  ))}
                  <Th className="text-right">{t('time.week.total')}</Th>
                </Tr>
              </THead>
              <TBody>
                {w.rows.map((r) => (
                  <Tr key={r.projectId}>
                    <Td>
                      <span className="flex items-center gap-2 font-bold">
                        <Dot colour={r.projectColour} /> {r.projectName}
                      </span>
                      <span className="block text-xs text-fg-3">{r.organisationName ?? t('projects.internal')}</span>
                    </Td>
                    {r.cells.map((c, i) => (
                      <Td key={i} className="text-right tabular-nums">
                        {c ? formatDuration(c) : ''}
                      </Td>
                    ))}
                    <Td className="text-right font-bold tabular-nums">{formatDuration(r.totalSeconds)}</Td>
                  </Tr>
                ))}
                <Tr>
                  <Td className="text-xs font-bold uppercase tracking-caps text-fg-3">{t('time.week.total')}</Td>
                  {w.dayTotals.map((c, i) => (
                    <Td key={i} className="text-right font-bold tabular-nums">
                      {c ? formatDuration(c) : ''}
                    </Td>
                  ))}
                  <Td className="text-right font-bold tabular-nums">{formatDuration(w.totalSeconds)}</Td>
                </Tr>
              </TBody>
            </Table>
          )}
        </section>
      ))}
    </div>
  );
}

function Billability({ filters, by, setBy, message }: { filters: Filters; by: 'month' | 'week' | 'person'; setBy: (b: 'month' | 'week' | 'person') => void; message: Msg }) {
  const { t } = useTranslation();
  const q = useQuery(billabilityQuery({ ...filters, by }));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  const pctTone = (p: number, goal: number | null) => (goal === null ? 'neutral' : p >= goal ? 'success' : 'warning');
  return (
    <div className="grid gap-4">
      <Field label={t('reports.by')} className="w-44">
        {(p) => (
          <NativeSelect {...p} value={by} onChange={(e) => setBy(e.target.value as typeof by)}>
            <option value="month">{t('reports.group.month')}</option>
            <option value="week">{t('reports.group.week')}</option>
            <option value="person">{t('reports.group.person')}</option>
          </NativeSelect>
        )}
      </Field>
      {!q.data ? (
        <Skeleton className="h-64" />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="p-4">
              <div className="text-xs font-bold uppercase tracking-caps text-fg-3">{t('reports.totals.hours')}</div>
              <div className="text-2xl font-bold tabular-nums">{formatDuration(q.data.totals.seconds)}</div>
              {q.data.totals.goalSeconds !== null && <div className="text-xs text-fg-3">{t('time.ofGoal', { goal: formatDuration(q.data.totals.goalSeconds) })}</div>}
            </Card>
            <Card className="p-4">
              <div className="text-xs font-bold uppercase tracking-caps text-fg-3">{t('reports.billability.percent')}</div>
              <div className="text-2xl font-bold tabular-nums">{q.data.totals.billablePercent}%</div>
              {q.data.totals.billableGoalPercent !== null && <div className="text-xs text-fg-3">{t('reports.billability.goal', { goal: q.data.totals.billableGoalPercent })}</div>}
            </Card>
            {q.data.pace && (
              <>
                <Card className="p-4">
                  <div className="text-xs font-bold uppercase tracking-caps text-fg-3">{t('reports.billability.year', { year: q.data.pace.year })}</div>
                  <div className="text-2xl font-bold tabular-nums">{formatHours(q.data.pace.yearSeconds, 'nl', 0)}</div>
                  <div className="text-xs text-fg-3">{t('reports.billability.criterion', { hours: formatHours(q.data.pace.yearlyGoalSeconds, 'nl', 0) })}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs font-bold uppercase tracking-caps text-fg-3">{t('reports.billability.pace')}</div>
                  <div className={cn('text-2xl font-bold tabular-nums', q.data.pace.aheadSeconds >= 0 ? 'text-success' : 'text-danger')}>
                    {q.data.pace.aheadSeconds >= 0 ? '+' : '−'}
                    {formatHours(Math.abs(q.data.pace.aheadSeconds), 'nl', 0)}
                  </div>
                  <div className="text-xs text-fg-3">{t('reports.billability.expected', { hours: formatHours(q.data.pace.expectedSecondsToDate, 'nl', 0) })}</div>
                </Card>
              </>
            )}
          </div>
          {!q.data.periods.length ? (
            <EmptyState title={t('reports.empty')} />
          ) : (
            <Table>
              <THead>
                <Tr>
                  <Th>{t(`reports.group.${by}`)}</Th>
                  <Th className="text-right">{t('reports.totals.hours')}</Th>
                  <Th className="text-right">{t('reports.totals.billable')}</Th>
                  <Th className="text-right">{t('reports.billability.percent')}</Th>
                  <Th className="text-right">{t('time.week.goal')}</Th>
                </Tr>
              </THead>
              <TBody>
                {q.data.periods.map((p) => (
                  <Tr key={p.key}>
                    <Td className="font-bold">{p.label}</Td>
                    <Td className="text-right tabular-nums">{formatDuration(p.seconds)}</Td>
                    <Td className="text-right tabular-nums">{formatDuration(p.billableSeconds)}</Td>
                    <Td className="text-right">
                      <Badge tone={pctTone(p.billablePercent, p.billableGoalPercent)}>{p.billablePercent}%</Badge>
                    </Td>
                    <Td className="text-right tabular-nums text-fg-2">{p.goalSeconds !== null ? formatDuration(p.goalSeconds) : '–'}</Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}

function Budget({ organisationId, projectId, message }: { organisationId?: string; projectId?: string; message: Msg }) {
  const { t } = useTranslation();
  const [includeClosed, setIncludeClosed] = useState(false);
  const q = useQuery(budgetQuery({ ...(organisationId && { organisationId }), ...(projectId && { projectId }), ...(includeClosed && { includeClosed: true }) }));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  const bar = (pct: number | null) =>
    pct === null ? null : (
      <span className="block h-1.5 w-24 overflow-hidden rounded-full bg-sunken">
        <span className={cn('block h-full', pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-success')} style={{ width: `${Math.min(100, pct)}%` }} />
      </span>
    );
  return (
    <div className="grid gap-3">
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={includeClosed} onCheckedChange={(v) => setIncludeClosed(v === true)} /> {t('reports.budget.includeClosed')}
      </label>
      {!q.data ? (
        <Skeleton className="h-64" />
      ) : !q.data.items.length ? (
        <EmptyState title={t('reports.budget.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('common.name')}</Th>
              <Th className="hidden md:table-cell">{t('org.eyebrow')}</Th>
              <Th className="text-right">{t('reports.budget.hours')}</Th>
              <Th className="text-right">{t('reports.budget.spentHours')}</Th>
              <Th>{t('reports.budget.percent')}</Th>
              <Th className="hidden text-right lg:table-cell">{t('reports.budget.money')}</Th>
              <Th className="hidden text-right lg:table-cell">{t('reports.budget.spentMoney')}</Th>
            </Tr>
          </THead>
          <TBody>
            {q.data.items.map((b) => (
              <Tr key={`${b.kind}:${b.id}`}>
                <Td>
                  <span className="font-bold">{b.name}</span> <Badge tone="neutral">{t(`reports.budget.kind.${b.kind}`)}</Badge>
                </Td>
                <Td className="hidden md:table-cell text-fg-2">{b.organisationName ?? t('projects.internal')}</Td>
                <Td className="text-right tabular-nums">{b.budgetMinutes !== null ? formatDuration(b.budgetMinutes * 60) : '–'}</Td>
                <Td className="text-right tabular-nums">{formatDuration(b.spentSeconds)}</Td>
                <Td>
                  <span className="flex items-center gap-2">
                    {bar(b.percentMinutes ?? b.percentCents)}
                    <span className="text-xs tabular-nums text-fg-2">{b.percentMinutes ?? b.percentCents ?? '–'}%</span>
                  </span>
                </Td>
                <Td className="hidden text-right tabular-nums lg:table-cell">{b.budgetCents !== null ? formatMoney(b.budgetCents) : '–'}</Td>
                <Td className="hidden text-right tabular-nums lg:table-cell">{formatMoney(b.spentCents)}</Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

/**
 * Unbilled work per client and engagement (CO-0300): the starting point for a new invoice. Rows that were billed in
 * another system can be selected and marked billed elsewhere (CO-1359).
 */
function Unbilled({ organisationId, to, message }: { organisationId?: string; to: string; message: Msg }) {
  const { t } = useTranslation();
  const canMark = useCan('billing:approve');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [marking, setMarking] = useState(false);
  const q = useQuery(unbilledQuery({ ...(organisationId && { organisationId }), to }));
  if (q.isError) return <ErrorState title={t('errors.generic')} description={message(q.error)} />;
  if (!q.data) return <Skeleton className="h-64" />;
  const d = q.data;
  const keyOf = (r: { organisationId: string; engagementId: string | null }) => `${r.organisationId}:${r.engagementId ?? ''}`;
  const chosen = d.items.filter((r) => selected.has(keyOf(r)));
  const toggle = (k: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(k);
    else next.delete(k);
    setSelected(next);
  };
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-fg-2">{t('reports.unbilled.hint', { date: formatDate(to) })}</p>
        {canMark && d.items.length > 0 && (
          <Button variant="secondary" size="sm" disabled={!chosen.length} onClick={() => setMarking(true)}>
            {t('billedElsewhere.open', { count: chosen.length })}
          </Button>
        )}
      </div>
      <BilledElsewhereDialog
        groups={marking ? chosen : []}
        to={to}
        onClose={() => setMarking(false)}
        onDone={() => {
          setMarking(false);
          setSelected(new Set());
        }}
      />
      {!d.items.length ? (
        <EmptyState title={t('reports.unbilled.empty')} />
      ) : (
        <Table>
          <THead>
            <Tr>
              {canMark && (
                <Th className="w-8">
                  <Checkbox
                    aria-label={t('billedElsewhere.selectAll')}
                    checked={chosen.length === d.items.length}
                    onCheckedChange={(v) => setSelected(v === true ? new Set(d.items.map(keyOf)) : new Set())}
                  />
                </Th>
              )}
              <Th>{t('org.eyebrow')}</Th>
              <Th className="hidden md:table-cell">{t('engagements.eyebrow')}</Th>
              <Th className="text-right">{t('reports.totals.hours')}</Th>
              <Th className="hidden text-right sm:table-cell">{t('reports.unbilled.time')}</Th>
              <Th className="hidden text-right lg:table-cell">{t('reports.unbilled.mileage')}</Th>
              <Th className="hidden text-right lg:table-cell">{t('reports.unbilled.terms')}</Th>
              <Th className="text-right">{t('reports.unbilled.total')}</Th>
              <Th className="hidden md:table-cell">{t('reports.unbilled.oldest')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {d.items.map((r) => (
              <Tr key={keyOf(r)}>
                {canMark && (
                  <Td>
                    <Checkbox
                      aria-label={t('billedElsewhere.select', { name: [r.organisationName, r.engagementName].filter(Boolean).join(' · ') })}
                      checked={selected.has(keyOf(r))}
                      onCheckedChange={(v) => toggle(keyOf(r), v === true)}
                    />
                  </Td>
                )}
                <Td className="font-bold">{r.organisationName}</Td>
                <Td className="hidden text-fg-2 md:table-cell">{r.engagementName ?? '–'}</Td>
                <Td className="text-right tabular-nums">{formatDuration(r.seconds)}</Td>
                <Td className="hidden text-right tabular-nums sm:table-cell">{formatMoney(r.timeCents, r.currency)}</Td>
                <Td className="hidden text-right tabular-nums lg:table-cell">{formatMoney(r.mileageCents, r.currency)}</Td>
                <Td className="hidden text-right tabular-nums lg:table-cell">{formatMoney(r.termCents, r.currency)}</Td>
                <Td className="text-right font-bold tabular-nums">{formatMoney(r.totalCents, r.currency)}</Td>
                <Td className="hidden tabular-nums md:table-cell">{r.firstDate ? formatDate(r.firstDate) : '–'}</Td>
                <Td className="text-right">
                  <Link to="/billing/invoices" search={{ organisationId: r.organisationId }} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
                    {t('reports.unbilled.invoice')}
                  </Link>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      <div className="flex flex-wrap gap-x-5 text-sm text-fg-2">
        <span>
          {t('reports.totals.hours')}: <span className="font-bold tabular-nums text-fg-1">{formatDuration(d.totals.seconds)}</span>
        </span>
        <span>
          {t('reports.unbilled.total')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(d.totals.totalCents)}</span>
        </span>
      </div>
    </div>
  );
}
